import { useEffect, useRef, useState } from 'react';
import { DropdownArrows16, Exit16 } from '@toptal/picasso-icons';
import { useQueryClient } from '@tanstack/react-query';
import { signOut } from '@/lib/auth-client';
import type { CurrentUser } from '@/hooks/useCurrentUser';
import { Tag } from '@toptal/picasso';
import type { UserRole } from '@app/shared';

type Props = {
  user: CurrentUser;
};

const ROLE_BADGE: Record<UserRole, { label: string; variant: 'yellow' | 'light-grey' | 'light-blue' }> = {
  admin: { label: 'Admin', variant: 'yellow' },
  consultant: { label: 'Consultant', variant: 'light-grey' },
  client: { label: 'Client', variant: 'light-blue' },
};

export function SidebarProfileMenu({ user }: Props) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const badge = ROLE_BADGE[user.role];

  return (
    <div ref={wrapperRef} className="relative">
      {open && (
        <div
          data-testid="sidebar-profile-menu"
          className="absolute bottom-full left-0 right-0 mb-2 rounded-lg border bg-card p-1 shadow-lg"
        >
          <MenuItem
            icon={<Exit16 className="h-4 w-4 fill-base-muted" />}
            label="Log Out"
            onClick={() => {
              setOpen(false);
              queryClient.clear();
              void signOut();
            }}
          />
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex w-full items-center gap-3 rounded-md hover:bg-sidebar-accent px-3 py-2 text-left text-sidebar-foreground transition-colors hover:bg-sidebar-accent/80"
      >
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{user.name}</div>
          <Tag.Rectangular variant={badge.variant}>{badge.label}</Tag.Rectangular>
        </div>
        <DropdownArrows16 className="h-4 w-4 text-base-muted fill-base-muted" />
      </button>
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-accent"
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
